import { useState, useMemo, useEffect } from "react";
import type { ClientInfo } from "@/features/accounts/account-group/hooks/use-kcloud-integration";

interface Params {
  onAuthenticate: (kikCode: string, userId: string, password: string) => Promise<ClientInfo[]>;
  onConnect: (selectedClient: ClientInfo) => Promise<void>;
  onClose: () => void;
}

export const useKCloudLogin = ({
  onAuthenticate,
  onConnect,
  onClose,
}: Params) => {
  const [kikCode, setKikCode] = useState("");
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [clientList, setClientList] = useState<ClientInfo[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientInfo | null>(null);
  const [showAllClients, setShowAllClients] = useState(false);

  const isFormValid =
    kikCode.trim() !== "" &&
    userId.trim() !== "" &&
    password.trim() !== "";

  const handleAuthenticate = async () => {
    if (!isFormValid) return;

    setIsConnecting(true);
    try {
      const clients = await onAuthenticate(kikCode, userId, password);
      setClientList(clients);
    } catch (error) {
      setClientList([]);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedClient) return;

    setIsConnecting(true);
    try {
      await onConnect(selectedClient);
      // onClose();
      reset();
    } finally {
      setIsConnecting(false);
    }
  };

  const reset = () => {
    setKikCode("");
    setUserId("");
    setPassword("");
    setClientList([]);
    setSelectedClient(null);
  };

  // showAllClients切り替え時に一番上の要素にチェックを付ける
  const handleShowAllClientsChange = () => {
    setShowAllClients((prev) => {
      const next = !prev;
      const nextList = next
        ? clientList
        : clientList.filter((client) => client.tantoFlag === 1 || client.tantoFlag === 0);
      const sortedList = [...nextList].sort((left, right) =>
        left.clientCode.localeCompare(right.clientCode, "ja")
      );
      if (sortedList.length > 0) {
        setSelectedClient(sortedList[0]);
      } else {
        setSelectedClient(null);
      }
      return next;
    });
  };

  const filteredClientList = useMemo(() => {
    const filtered = showAllClients
      ? clientList
      : clientList.filter((client) => client.tantoFlag === 1 || client.tantoFlag === 0);

    return [...filtered].sort((left, right) =>
      left.clientCode.localeCompare(right.clientCode, "ja"),
    );
  }, [clientList, showAllClients]);

  useEffect(() => {
    if (filteredClientList.length === 0) {
      setSelectedClient(null);
      return;
    }

    const isSelectedInList =
      selectedClient !== null &&
      filteredClientList.some((client) => client.uniClientCode === selectedClient.uniClientCode);

    if (!isSelectedInList) {
      setSelectedClient(filteredClientList[0]);
    }
  }, [filteredClientList, selectedClient]);

  return {
    kikCode,
    userId,
    password,
    isConnecting,
    clientList,
    selectedClient,
    showAllClients,
    filteredClientList,
    isFormValid,

    setKikCode,
    setUserId,
    setPassword,
    setSelectedClient,
    setShowAllClients,

    handleAuthenticate,
    handleConnect,
    handleShowAllClientsChange,
  };
};